import { memo, useCallback, useMemo, useRef, ReactElement } from 'react';
import { DeleteOutlined, EditOutlined, FormOutlined, EyeOutlined, PlusOutlined } from '@ant-design/icons';
import { Button, Flex, Space, Tooltip } from 'antd';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';

import { Table, TableComponent, TableProps } from '@ohpen/enso-ui';
import { ActionEntityType, IntermediaryGetAllResponse } from '@pmp/api';

import { columns } from '../tableConfig';
import { Guard, useGuard } from '@ohpen/react-authz';
import { ListProps } from '@pmp/types';
import { lockActionTitle } from '@pmp/utils';

const entityType = ActionEntityType.BROKERS;

export const BrokersList = memo(
  ({
    isFetching,
    valueLists,
    data,
    onCreate,
    onView,
    onEdit,
    onDelete,
    onOpenInPanel,
  }: ListProps<IntermediaryGetAllResponse> & { onOpenInPanel: (record: IntermediaryGetAllResponse) => void }): ReactElement => {
    const tableRef = useRef<TableComponent | null>(null);
    const { allowed: showRowSelection } = useGuard({ flags: ['enableExports'] });

    const { t } = useTranslation();

    const onAdd = useCallback(() => {
      onCreate();
    }, [onCreate]);

    const getRowActions: (record: IntermediaryGetAllResponse) => (ReactElement | null)[] = useCallback(
      record => {
        const rowActions = [
          <Tooltip key="view" title={t('global.buttons.view')} placement="top">
            <Button type="text" aria-label="View intermediary" icon={<EyeOutlined />} onClick={() => onView(record)} />
          </Tooltip>,
          <Guard key="edit" permissions={['brokers:update']} flags={['enableBrokersCrud']}>
            <Tooltip key="edit" title={lockActionTitle('edit', record.lockedByActionId)} placement="top">
              <Button type="text" aria-label="Edit intermediary" icon={<EditOutlined key="edit" />} onClick={() => onEdit(record)} disabled={!!record.lockedByActionId} />
            </Tooltip>
          </Guard>,
          <Guard key="delete" permissions={['brokers:delete']} flags={['enableBrokersCrud']}>
            <Tooltip title={lockActionTitle('delete', record.lockedByActionId)} placement="top">
              <Button type="text" aria-label="Delete intermediary" icon={<DeleteOutlined key="delete" />} onClick={() => onDelete(record)} disabled={!!record.lockedByActionId} />
            </Tooltip>
          </Guard>,
          <Tooltip key="panel" title={t('lender.brokers.labels.openInPanel')} placement="top">
            <Button type="text" aria-label="Open in Panel" icon={<FormOutlined key="edit" />} onClick={() => onOpenInPanel(record)} />
          </Tooltip>,
          null,
        ];

        return rowActions;
      },
      [onDelete, onEdit, onOpenInPanel, onView, t],
    );

    const tableProps: TableProps<ActionEntityType, IntermediaryGetAllResponse> = useMemo(
      () => ({
        id: entityType,
        ref: tableRef,
        dataSource: data ?? [],
        columns,
        rowKey: 'id',
        pagination: true,
        scroll: { x: 'max-content', y: '100%' },
        loading: isFetching,
        getRowActions,
        showRowSelection,
        showViews: false,
        valueLists,
      }),
      [data, getRowActions, isFetching, valueLists, showRowSelection],
    );

    return (
      <Table {...tableProps}>
        <Table.ActionsBar>
          <Flex justify="space-between">
            <Space size="middle">
              <Table.GlobalSearch />
            </Space>
            <Space size="middle">
              <Table.ResetFiltersButton />
              <Table.SelectColumnsButton />
              <Guard flags={['enableExports']}>
                <Table.ExportButton
                  editableFileName
                  fileName={`Intermediaries ${dayjs(new Date()).format('YYYY-MM-DD')}`}
                  sheetName="Intermediaries"
                  disabled={isFetching}
                  exportView
                />
              </Guard>
              <Guard permissions={['brokers:create']} flags={['enableBrokersCrud']}>
                <Button icon={<PlusOutlined />} onClick={onAdd} type="primary">
                  {t('global.labels.create')}
                </Button>
              </Guard>
            </Space>
          </Flex>
        </Table.ActionsBar>
        <Table.BulkBar xItemsSelectedLabel="lender.brokers.labels.xBrokersSelected">
          <Space size="middle">
            <Guard flags={['enableExports']}>
              <Table.ExportButton
                editableFileName
                fileName={`Intermediaries ${dayjs(new Date()).format('YYYY-MM-DD')}`}
                sheetName="Intermediaries"
                disabled={isFetching}
                exportView
              />
            </Guard>
          </Space>
        </Table.BulkBar>
      </Table>
    );
  },
);
